import { LineItemCode } from '@prisma/client';
import React, { FC, ReactElement } from 'react';
import { Box } from '@material-ui/core';
import { Divider, IconSubtractFilled, ItemizedListing, TypographyWrapper } from '..';
import {
  CoreBundleItem,
  ItemStatus,
  Participant,
} from '../../types/apollo/generated/types.generated';
import { CheckoutLineItem } from '../../types/models/lineItem';
import { ListingWithImages } from '../../types/sharetribe/listing';

import css from './ItemizedListingBreakdown.module.css';
import {
  itemHasBeenCanceled,
  itemHasBeenDisputedAndNotVerified,
  itemHasBeenVerified,
  itemHasPassedDisputeAcceptedAndBundleIsReturnable,
} from '../../util/bundleItem';
import { TypographyFormat } from '../TypographyWrapper/TypographyWrapper';

interface DeleteButtonProps {
  onClick: () => void;
}

const DeleteButton: FC<DeleteButtonProps> = (props: DeleteButtonProps) => {
  const { onClick } = props;

  return (
    <button className={css.removeListingButton} onClick={onClick} type="button">
      <IconSubtractFilled className={css.removeIcon} />
    </button>
  );
};

interface ItemizedListingBreakdownProps {
  // Either pass in a list of listings or tranasctions. Listings are passed in
  // directly when there are no transactions yet (Checkout Phase)
  bundleItems?: CoreBundleItem[];
  listings: ListingWithImages[];
  lineItems: CheckoutLineItem[];
  referrerForLogging: string;
  title?: ReactElement | string;
  handleRemoveListings?: (listingIds: string[]) => void;
  participant?: Participant;
  isBundleReturnable?: boolean;
  bundleExpirationDate?: string;
}

const ItemizedListingBreakdown: FC<ItemizedListingBreakdownProps> = (props) => {
  const {
    listings,
    lineItems,
    title,
    referrerForLogging,
    handleRemoveListings,
    participant,
    bundleItems,
    isBundleReturnable,
    bundleExpirationDate,
  } = props;

  let listingsAndMaybeBundleItems: {
    bundleItem?: CoreBundleItem;
    listing: ListingWithImages;
  }[] = [];

  if (bundleItems) {
    const listingsAndBundleItems: {
      bundleItem: CoreBundleItem;
      listing: ListingWithImages;
    }[] = [];

    bundleItems.forEach((bundleItem) => {
      // Find the corresponding listing in the listings array
      const bundleItemListing = listings.find(
        (listing) => listing.id.uuid === bundleItem.listing.sharetribeListingId
      );

      // Only push into the array if the listing exists
      if (bundleItemListing) {
        listingsAndBundleItems.push({
          listing: bundleItemListing,
          bundleItem,
        });
      }
    });

    listingsAndMaybeBundleItems = listingsAndBundleItems;
  } else {
    listingsAndMaybeBundleItems = listings.map((listing) => ({
      listing,
    }));
  }

  const nonReturnedBundleItems =
    bundleItems?.filter((bundleItem) => bundleItem.status !== ItemStatus.DisputeAccepted) ?? [];
  const hasDisputedBundleItem = bundleItems?.some(
    (bundleItem) => bundleItem.status === ItemStatus.DisputeAccepted
  );
  const currentDateIsBeforeBundleExpirationDate =
    bundleExpirationDate && new Date() < new Date(bundleExpirationDate);
  const numberOfBundleItems = bundleItems?.length ?? 0;

  const canReturnMoreBundleItems =
    isBundleReturnable &&
    currentDateIsBeforeBundleExpirationDate &&
    hasDisputedBundleItem &&
    nonReturnedBundleItems.length > 0 &&
    nonReturnedBundleItems.length < numberOfBundleItems;

  return (
    <>
      {title && (
        <>
          <div className={css.bundleTitle}>{title}</div>
          <Divider />
        </>
      )}

      {canReturnMoreBundleItems && (
        <Box mt={3} px={2}>
          <TypographyWrapper variant="body2">
            If you need assistance with returning other items, please contact us at{' '}
            <a href="mailto:support@treet.co">
              <TypographyWrapper variant="body2" format={TypographyFormat.Underlined}>
                support@treet.co
              </TypographyWrapper>
            </a>
          </TypographyWrapper>
        </Box>
      )}

      {listingsAndMaybeBundleItems.map(({ listing, bundleItem }, index) => {
        const listingLineItem = lineItems.find(
          (lineItem) =>
            lineItem.code === LineItemCode.LISTING &&
            lineItem.listingLineItem?.sharetribeListingId === listing.id.uuid
        );

        const isDisputed = bundleItem ? itemHasBeenDisputedAndNotVerified(bundleItem) : false;
        const isCanceled = bundleItem ? itemHasBeenCanceled(bundleItem) : false;
        const isVerified = bundleItem ? itemHasBeenVerified(bundleItem) : false;
        const isReturned = bundleItem
          ? itemHasPassedDisputeAcceptedAndBundleIsReturnable(bundleItem, isBundleReturnable)
          : false;

        const actionEl = handleRemoveListings ? (
          <DeleteButton onClick={() => handleRemoveListings([listing.id.uuid])} />
        ) : undefined;

        return (
          <div key={listing.id.uuid}>
            {index !== 0 && <Divider />}
            <ItemizedListing
              isDisputed={isDisputed}
              isCanceled={isCanceled}
              isVerified={isVerified}
              isReturned={isReturned}
              listing={listing}
              bundleItem={bundleItem}
              lineItem={listingLineItem}
              referrerForLogging={referrerForLogging}
              actionEl={actionEl}
              participant={participant}
            />
          </div>
        );
      })}
    </>
  );
};

export default ItemizedListingBreakdown;
